import { apiClient, apiFormDataRequest } from "../index";
import { getTokenFromCookie } from "./authService";

// Use Vite-style env variable; fallback to same-origin /api if not provided
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

// Topology API endpoints
const CHAT_PATH = "/api/v1/topology/chat";
const CHAT_STREAM_PATH = "/api/v1/topology/chat/stream";
const CHAT_RESUME_PATH = "/api/v1/topology/resume";
const CHAT_UPLOAD_PATH = "/api/v1/topology/chat/upload";
const CHAT_IMPORT_PATH = "/api/v1/topology/chat/import";
const CHAT_STOP_PATH = "/api/v1/topology/chat/stop";

const STATE_PATH = "/api/v1/topology/state";
const CANVAS_PATH = "/api/v1/topology/canvas";

const MESSAGES_PATH = "/api/v1/topology/messages";

const EDIT_MESSAGE_PATH = "/api/v1/topology/edit-message";
const REVERT_PATH = "/api/v1/topology/revert";

const PLAN_LIST_PATH = "/api/v1/topology/plan-list";
const PLAN_VERSION_PATH = "/api/v1/topology/plan-version";

const THREADS_PATH = "/api/v1/topology/threads";

const SUBMIT_TOPOLOGY_PATH = "/api/v1/topology/submit-topology";
const GENERATE_SUMMARY_PATH = "/api/v1/topology/generate-summary";
const SAVED_TOPOLOGIES_PATH = "/api/v1/topology/saved-topologies";
const DEFAULT_TOPOLOGIES_PATH = "/api/v1/topology/default-topologies";

const DEVICE_TYPES_PATH = "/api/v1/topology/device-types/";
const DEVICE_CATEGORIES_PATH = "/api/v1/topology/device-types/categories/";

// send a chat message to the topology chatbot
export async function sendChatMessage(
  threadId,
  message,
  mode,
  canvas = null,
  imageUrl = null,
  checkpointId = null,
) {
  const normalizedMode = mode ? mode.toLowerCase() : "agent";

  const payload = {
    thread_id: threadId,
    message,
    mode: normalizedMode,
  };

  if (canvas) {
    payload.canvas_json = canvas;
  }

  if (imageUrl) {
    payload.image_url = imageUrl;
  }

  if (checkpointId) {
    payload.checkpoint_id = checkpointId;
  }

  console.log("[chat submit] payload", payload);
  const response = await apiClient.post(CHAT_PATH, payload);
  console.log("[chat submit] response", response);
  return response;
}

// send a chat message via streaming (Server-Sent Events)
export async function sendChatMessageStream(
  threadId,
  message,
  mode,
  canvas = null,
  imageUrl = null,
  checkpointId = null,
  onEvent = null,
  abortController = null,
) {
  const normalizedMode = mode ? mode.toLowerCase() : "agent";

  const payload = {
    thread_id: threadId,
    message,
    mode: normalizedMode,
  };

  if (canvas) {
    payload.canvas_json = canvas;
  }

  if (imageUrl) {
    payload.image_url = imageUrl;
  }

  if (checkpointId) {
    payload.checkpoint_id = checkpointId;
  }

  // Get access token for Bearer auth (required for fetch streaming)
  const accessToken = getTokenFromCookie("access_token");

  // Use fetch with ReadableStream for streaming (supports Bearer tokens)
  const response = await fetch(`${API_BASE_URL}${CHAT_STREAM_PATH}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
    },
    body: JSON.stringify(payload),
    signal: abortController?.signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Stream request failed: ${response.status} - ${errorText}`);
  }

  // Check if response is streaming
  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("text/event-stream")) {
    throw new Error("Response is not a stream");
  }

  console.log(
    "[Stream] Stream connection established, content-type:",
    contentType,
  );

  // Store stream transcript for debugging
  const streamTranscript = [];

  // Parse SSE stream
  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // SSE events are separated by double newlines
      const events = buffer.split("\n\n");
      buffer = events.pop() || ""; // Keep incomplete event in buffer

      for (const eventText of events) {
        if (!eventText.trim()) continue;

        let eventType = "message";
        let eventData = null;

        // Parse SSE event format: "event: <type>\ndata: <json>"
        const lines = eventText.split("\n");
        for (const line of lines) {
          const trimmed = line.trim();
          if (trimmed.startsWith("event:")) {
            eventType = trimmed.substring(6).trim();
          } else if (trimmed.startsWith("data:")) {
            const dataStr = trimmed.substring(5).trim();
            try {
              eventData = JSON.parse(dataStr);
            } catch (e) {
              console.warn("[Stream] Failed to parse event data:", dataStr, e);
              continue;
            }
          }
        }

        // Process the event if we have data
        if (eventData) {
          console.log("[Stream] Received event:", eventData.type, eventData);
          // Store in transcript
          streamTranscript.push({
            timestamp: new Date().toISOString(),
            eventType,
            data: eventData,
          });

          // Call onEvent callback if provided
          if (onEvent) {
            onEvent(eventData);
          }
        }
      }
    }

    // Process any remaining buffer content as a final event
    if (buffer.trim()) {
      let eventType = "message";
      let eventData = null;

      const lines = buffer.split("\n");
      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("event:")) {
          eventType = trimmed.substring(6).trim();
        } else if (trimmed.startsWith("data:")) {
          const dataStr = trimmed.substring(5).trim();
          try {
            eventData = JSON.parse(dataStr);
          } catch (e) {
            console.warn(
              "[Stream] Failed to parse final event data:",
              dataStr,
              e,
            );
          }
        }
      }

      if (eventData) {
        streamTranscript.push({
          timestamp: new Date().toISOString(),
          eventType,
          data: eventData,
        });

        if (onEvent) {
          onEvent(eventData);
        }
      }
    }
  } catch (error) {
    if (error.name === "AbortError") {
      console.log("[Stream] Request aborted");
      throw error;
    }
    throw error;
  } finally {
    reader.releaseLock();
  }

  // Return transcript for debugging
  return {
    transcript: streamTranscript,
  };
}

// resume chat session with topology chatbot (interrupted by plan mode)
export async function resumeChatMessage(
  threadId,
  resumeAction,
  userFeedback = null,
) {
  const payload = {
    thread_id: threadId,
    resume_action: resumeAction,
  };

  if (userFeedback) {
    payload.user_feedback = userFeedback;
  }

  return apiClient.post(CHAT_RESUME_PATH, payload);
}

// stop an active streaming generation for a thread
// Response: { status: "stopped" } or { status: "not_running" }
export async function stopChatGeneration(threadId) {
  return apiClient.post(CHAT_STOP_PATH, { thread_id: threadId });
}

// upload a topology image to the topology chatbot (multipart/form-data)
export async function uploadTopologyImage(
  threadId,
  mode = "vision",
  message,
  imageFile,
  canvas = null,
  checkpointId = null,
) {
  const normalizedMode = mode ? mode.toLowerCase() : "vision";

  const formData = new FormData();
  formData.append("thread_id", threadId);
  formData.append("mode", normalizedMode);
  formData.append("message", message || "");
  formData.append("image", imageFile);

  if (canvas) {
    formData.append(
      "canvas_json",
      typeof canvas === "string" ? canvas : JSON.stringify(canvas),
    );
  }

  if (checkpointId) {
    formData.append("checkpoint_id", checkpointId);
  }

  return apiFormDataRequest(CHAT_UPLOAD_PATH, formData);
}

// import a topology JSON file via the topology chatbot (multipart/form-data)
export async function importTopologyJson(
  threadId,
  jsonFile,
  checkpointId = null,
) {
  const formData = new FormData();
  formData.append("thread_id", threadId);
  formData.append("json_file", jsonFile);

  if (checkpointId) {
    formData.append("checkpoint_id", checkpointId);
  }

  return apiFormDataRequest(CHAT_IMPORT_PATH, formData);
}

// get state for a specific thread
export async function getThreadState(threadId, includeMessages = false) {
  const params = includeMessages ? "?include_messages=true" : "";
  return apiClient.get(`${STATE_PATH}/${threadId}${params}`);
}

// get canvas for a specific thread
export async function getThreadCanvas(threadId) {
  return apiClient.get(`${CANVAS_PATH}/${threadId}`);
}

// sync canvas for a specific thread
export async function syncThreadCanvas(threadId, canvas) {
  return apiClient.put(`${CANVAS_PATH}/${threadId}`, canvas);
}

// get messages for a specific thread
export async function getThreadMessages(threadId) {
  return apiClient.get(`${MESSAGES_PATH}/${threadId}`);
}

// revert to checkpoint via edit-message
export async function revertToCheckpoint(
  threadId,
  new_content,
  { message_index = null, checkpoint_id = null } = {},
) {
  const payload = { thread_id: threadId, new_content };
  if (message_index != null) payload.message_index = message_index;
  if (checkpoint_id != null) payload.checkpoint_id = checkpoint_id;
  console.log("[revert to checkpoint] payload", payload);
  const response = await apiClient.post(EDIT_MESSAGE_PATH, payload);
  console.log("[revert to checkpoint] response", response);
  return response;
}

// list all threads for the authenticated user
export async function listThreads() {
  return apiClient.get(THREADS_PATH);
}

// create a new thread for the authenticated user
export async function createThread() {
  return apiClient.post(THREADS_PATH);
}

// delete a thread for the authenticated user
export async function deleteThread(threadId) {
  return apiClient.del(`${THREADS_PATH}/${threadId}`);
}

// submit a topology for a specific thread (optional name for the saved topology)
export async function submitTopology(threadId, name = null) {
  const payload = { thread_id: threadId };
  if (name != null && String(name).trim() !== "") {
    payload.name = String(name).trim();
  }
  return apiClient.post(SUBMIT_TOPOLOGY_PATH, payload);
}

// generate AI title/description for the current canvas without saving
export async function generateTopologySummary(canvasJson, name = "") {
  return apiClient.post(GENERATE_SUMMARY_PATH, {
    canvas_json: canvasJson,
    name: name || "",
  });
}

// get saved topologies for the authenticated user
export async function getSavedTopologies() {
  return apiClient.get(SAVED_TOPOLOGIES_PATH);
}

// get default topologies
export async function getDefaultTopologies(includeUnpublished = false) {
  const includeUnpublishedParam = includeUnpublished ? "true" : "false";
  return apiClient.get(
    `${DEFAULT_TOPOLOGIES_PATH}?include_unpublished=${includeUnpublishedParam}`,
  );
}

// get all device types
export async function getDeviceTypes() {
  return apiClient.get(DEVICE_TYPES_PATH);
}

// get all device categories
export async function getDeviceCategories() {
  return apiClient.get(DEVICE_CATEGORIES_PATH);
}
// get canvas state at a specific checkpoint (read-only revert)
export async function getCanvasAtCheckpoint(threadId, { checkpointId = null, messageIndex = null } = {}) {
  const payload = { thread_id: threadId };
  if (checkpointId) payload.checkpoint_id = checkpointId;
  if (messageIndex != null) payload.message_index = messageIndex;
  return apiClient.post(REVERT_PATH, payload);
}

// get list of plan versions for a thread (metadata only, no plan_md)
export async function getPlanList(threadId) {
  return apiClient.get(`${PLAN_LIST_PATH}/${threadId}`);
}

// get full plan_md for a specific version number
export async function getPlanVersion(threadId, version) {
  return apiClient.get(`${PLAN_VERSION_PATH}/${threadId}/${version}`);
}
